/**
 * useLastStudioPath
 *
 * Persists the last route visited inside Klviar Studio (admin-console) so that
 * when the user switches from Chat back to Studio they land on the same page
 * instead of always going to `/` (Dashboard).
 *
 * Storage: localStorage key `klviar:lastStudioPath`.
 */

const STORAGE_KEY = 'klviar:lastStudioPath';

/** Routes that should never be persisted as "last location". */
const EXCLUDED_PREFIXES = ['/auth/', '/login', '/logout'];

function isExcluded(path: string): boolean {
  return EXCLUDED_PREFIXES.some((p) => path.startsWith(p));
}

/** Save the current Studio path. Call from within Studio on every route change. */
export function rememberStudioPath(path: string): void {
  if (typeof window === 'undefined') return;
  if (!path || isExcluded(path)) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, path);
  } catch {
    // localStorage may be blocked (private mode); fail silently.
  }
}

/**
 * Build the URL to switch back to Studio from another app (e.g. Chat).
 * Returns the saved path appended to `/` (Studio is hosted at the root), or `/`
 * if nothing was saved or storage is unavailable.
 */
export function getStudioReturnUrl(): string {
  if (typeof window === 'undefined') return '/';
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && saved.startsWith('/') && !isExcluded(saved)) {
      return saved;
    }
  } catch {
    // ignore
  }
  return '/';
}
